import React, { useState } from 'react';
import { FaEnvelope, FaPaperPlane } from 'react-icons/fa';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Card } from '../components/ds/core/Card';
import { Badge } from '../components/ds/core/Badge';
import { Button } from '../components/ds/core/Button';
import './AboutView.css';

const cardStyle = {
  background: '#e6e7eb',
  color: 'var(--text-body)',
  border: '1px solid var(--border-subtle)',
  boxShadow: 'var(--shadow-md)',
  backdropFilter: 'none',
};

const inputStyle = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  marginTop: '0.35rem',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border-strong, #bbb)',
  background: '#fff',
  color: '#222',
  fontSize: '1rem',
  fontFamily: 'inherit',
};

function ContactView() {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', text: '' });

    if (!email.trim() || !subject.trim() || !message.trim()) {
      setFeedback({ type: 'error', text: 'Please fill in all fields.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFeedback({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    // Rate limit: at most one message per 60s, and 5 per day (per browser)
    const now = Date.now();
    const last = parseInt(localStorage.getItem('yapp_contact_last') || '0', 10);
    if (now - last < 60000) {
      setFeedback({ type: 'error', text: 'Please wait a minute before sending another message.' });
      return;
    }
    const dayKey = new Date().toISOString().slice(0, 10);
    let count = 0;
    try {
      const stored = JSON.parse(localStorage.getItem('yapp_contact_count') || '{}');
      if (stored.day === dayKey) count = stored.n || 0;
    } catch (e) { /* ignore */ }
    if (count >= 5) {
      setFeedback({ type: 'error', text: 'You\'ve reached the daily limit for messages. Please try again tomorrow.' });
      return;
    }

    setSending(true);
    try {
      await addDoc(collection(db, 'contactMessages'), {
        email: email.trim().slice(0, 200),
        subject: subject.trim().slice(0, 200),
        message: message.trim().slice(0, 5000),
        starred: false,
        createdAt: serverTimestamp(),
      });
      localStorage.setItem('yapp_contact_last', String(now));
      localStorage.setItem('yapp_contact_count', JSON.stringify({ day: dayKey, n: count + 1 }));
      setFeedback({ type: 'success', text: 'Thanks! Your message has been sent. We\'ll get back to you soon.' });
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      console.error('Failed to send contact message:', err);
      setFeedback({ type: 'error', text: 'Something went wrong sending your message. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="about-view">
      <div className="about-container">
        <div className="about-header">
          <Badge tone="brand" icon={<FaEnvelope />}>Contact Us</Badge>
          <h1>Get in touch</h1>
          <p>Questions, feedback, or an issue? Send us a message and we'll reply by email.</p>
        </div>

        <div className="about-content">
          <Card variant="glass" padding="lg" className="about-section" style={cardStyle}>
            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 600 }}>
                Your email address
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={sending}
                  maxLength={200}
                  style={inputStyle}
                />
              </label>

              <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 600 }}>
                Subject
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="What's this about?"
                  disabled={sending}
                  maxLength={200}
                  style={inputStyle}
                />
              </label>

              <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 600 }}>
                Message
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your message…"
                  disabled={sending}
                  maxLength={5000}
                  rows={6}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </label>

              {feedback.text && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.6rem 0.9rem',
                    borderRadius: '10px',
                    fontWeight: 600,
                    background: feedback.type === 'success' ? '#e8f5e9' : '#fdecea',
                    color: feedback.type === 'success' ? '#2e7d32' : '#c0392b',
                  }}
                >
                  {feedback.text}
                </div>
              )}

              <Button type="submit" variant="primary" size="lg" icon={<FaPaperPlane />} disabled={sending}>
                {sending ? 'Sending…' : 'Send message'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ContactView;
